import * as React from 'react';

import Grid from '@mui/material/Grid';

import { AxiosError } from 'axios';
import AtomButton from 'portal-common-library/atoms/AtomButton';
import AtomTextField from 'portal-common-library/atoms/AtomTextField';
import { Cookies } from 'react-cookie';

import { AtomLinkButton } from '@app/common/atoms';
import { TIME_IN_MILLISECONDS, PEEK_COOKIE } from '@app/common/constants';
import { shouldShowPeekHeader } from '@app/common/helpers';
import { IErrors, TInputChangeEvent } from '@app/common/interfaces';

import { IPeekHeaderState, TPeekHeaderProps } from './interfaces';
import './styles.scss';
import { createImpersonateSession, deleteImpersonateSession } from './utils';

class PeekHeader extends React.Component<TPeekHeaderProps, IPeekHeaderState> {
  private readonly cookies: Cookies;

  constructor(props: TPeekHeaderProps) {
    super(props);
    this.state = {
      publisherIdTextField: '',
    };

    this.cookies = new Cookies();
  }

  public handleOnChange = (e: TInputChangeEvent): void => {
    this.setState({ publisherIdTextField: e.target.value });
  };

  public handleCreate = (): void => {
    let { fetchPublisher, findUser, openSnackbar } = this.props;

    createImpersonateSession(this.state.publisherIdTextField)
      .then(() => {
        const expirationDate: Date = new Date();
        expirationDate.setMonth(expirationDate.getMonth() + 6);

        if (!this.cookies.get(PEEK_COOKIE)) {
          this.cookies.set(PEEK_COOKIE, true, {
            path: '/',
            expires: expirationDate,
          });
        }

        void Promise.all([fetchPublisher(), findUser()]).then(() => {
          this.props.history.push('/dashboard');
        });
      })
      .catch((error: AxiosError<IErrors>) => {
        let e = error.response ? error.response.data.errors : error;

        openSnackbar({
          text: e as string,
          autoHideDuration: 2 * TIME_IN_MILLISECONDS.SECOND,
          anchorOriginVertical: 'bottom',
          anchorOriginHorizontal: 'left',
        });
      });
  };

  public handleDelete = (): void => {
    let { fetchPublisher, findUser, openSnackbar } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const peekCookie = this.cookies.get(PEEK_COOKIE);

    if (!peekCookie) {
      // early return so 'Turn Off' doesn't log user out
      return this.props.history.push('/dashboard');
    }

    deleteImpersonateSession()
      .then(() => {
        if (peekCookie) {
          this.cookies.remove(PEEK_COOKIE, { path: '/' });
          void Promise.all([fetchPublisher(), findUser()]).then(() => {
            this.props.history.push('/dashboard');
            location.reload();
          });
        }
      })
      .catch((error: AxiosError<IErrors>) => {
        let e = error.response ? error.response.data.errors : error;

        openSnackbar({
          text: e as string,
          autoHideDuration: 2 * TIME_IN_MILLISECONDS.SECOND,
          anchorOriginVertical: 'bottom',
          anchorOriginHorizontal: 'left',
        });
      });
  };

  public renderSelectCompanyOrName = (id: number, name: string | null): JSX.Element => {
    let className: string = 'peek-header__select-company-text';

    if (id !== 1 && name) {
      return (
        <div className={className} data-qa="peek-header__company-name">
          {name}
        </div>
      );
    }

    return (
      <div className={`${className} italics`} data-qa="peek-header__company-name">
        {'Select Company'}
      </div>
    );
  };

  public handleSearchCompanyOnClick = (): void => {
    this.props.history.push('/peek');
  };

  public handleEnterKey = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleCreate();
    }
  };

  public render(): JSX.Element {
    const { publisher, user } = this.props;

    if (!shouldShowPeekHeader(user, publisher)) {
      return <></>;
    }

    return (
      <Grid container={true} className="peek-header" data-qa="peek-header__container">
        <Grid item={true} xs={12} sm={9} className="peek-header__peek-section">
          <div className="peek-header__peek-text">PEEK</div>
          <div>
            <div className="peek-header__peek-at-text">You are peeking at:</div>
            {this.renderSelectCompanyOrName(publisher.id, publisher.name)}
          </div>
          <div className="peek-header__search-and-peek-button-container">
            <div className="peek-header__peek-input-container">
              <AtomTextField
                id="peek-header__text-input"
                label=""
                placeholder="Publisher ID"
                attributeName="publisherId"
                value={this.state.publisherIdTextField}
                onChange={this.handleOnChange}
                dataAttributes={{ 'data-qa': 'peek-header__text-input' }}
                type="number"
                onKeyPress={this.handleEnterKey}
              />
            </div>
            <div className="peek-header__peek-button-container">
              <AtomButton
                id="peek-header__select-company-button-text"
                dataAttributes={{ 'data-qa': 'peek-header__select-company-cta' }}
                buttonType="primary"
                buttonContent="Peek"
                onClick={this.handleCreate}
              />
            </div>
            <div className="peek-header__search-companies-button-container">
              <AtomLinkButton
                id="peek-header__search-companies-text"
                text="Search Companies"
                onClick={this.handleSearchCompanyOnClick}
              />
            </div>
          </div>
        </Grid>
        <Grid item={true} xs={12} sm={3} className="peek-header__cta-section">
          <AtomButton
            id="peek-header__turn-off-button"
            dataAttributes={{ 'data-qa': 'peek-header__turn-off-cta' }}
            buttonType="secondary"
            buttonContent={'Turn Off'}
            onClick={this.handleDelete}
          />
        </Grid>
      </Grid>
    );
  }
}

export default PeekHeader;
